import { BreadcrumbJsonLd, ProductJsonLd } from 'next-seo';
import PropTypes from 'prop-types';

import { getOfferImageLink } from 'lib/image';
import { toAbsoluteLink, toCategoryLink, toOfferLink } from 'lib/links';
import { toFormattedOfferOldPrice, toFormattedOfferPrice } from 'lib/offer';
import placeholder from 'lib/placeholder';
import {
  appendDynamicSubcategoryToBreadcrumbSchema,
  toReviewSchema,
} from 'lib/schema';
import { toFormattedJsonLdString } from 'lib/string';

import APP from 'constants/app';
import OFFER from 'constants/offer';

const Schema = ({
  categoryName = null,
  categorySlug,
  offerId = null,
  offerInstructionsRaw = null,
  offerOldPrice,
  offerPhoto = null,
  offerPrice = null,
  offerPriceType,
  offerSlug = null,
  offerTitle = null,
  reviewRating = null,
  reviews = [],
  storeDomain = null,
  storeName = null,
  subcategoryName = null,
  subcategorySlug = null,
  totalReviews = null,
}) => (
  <>
    <BreadcrumbJsonLd
      itemListElements={appendDynamicSubcategoryToBreadcrumbSchema(
        [
          {
            position: 1,
            name: placeholder('breadcrumbs.app'),
            item: APP.URL,
          },
          {
            position: 2,
            name: categoryName,
            item: toCategoryLink(categorySlug),
          },
          {
            position: 3,
            name: toFormattedJsonLdString(offerTitle),
            item: toAbsoluteLink(toOfferLink(offerSlug)),
          },
        ],
        subcategoryName,
        subcategorySlug
      )}
    />
    <ProductJsonLd
      aggregateRating={
        totalReviews > 0
          ? {
              ratingValue: reviewRating || OFFER.REVIEWS.DEFAULT_RATING_VALUE,
              reviewCount: totalReviews,
            }
          : null
      }
      description={offerInstructionsRaw}
      images={[getOfferImageLink(offerPhoto, 268)]}
      offers={[
        {
          price: offerPrice,
          priceCurrency: 'BRL',
          availability: 'InStock',
          itemCondition: 'NewCondition',
          url: storeDomain,
          sellerName: storeName,
          offerCount: 1,
          lowPrice: toFormattedOfferPrice(offerPrice, offerPriceType),
          highPrice: toFormattedOfferOldPrice(offerOldPrice, offerPriceType),
          category: categoryName,
          seller: { name: storeName },
        },
      ]}
      productName={toFormattedJsonLdString(offerTitle)}
      reviews={totalReviews > 0 ? toReviewSchema(reviews, offerTitle) : null}
      sku={offerId}
    />
  </>
);

Schema.propTypes = {
  categoryName: PropTypes.string,
  categorySlug: PropTypes.string.isRequired,
  offerId: PropTypes.number,
  offerInstructionsRaw: PropTypes.string,
  offerOldPrice: PropTypes.number,
  offerPhoto: PropTypes.string,
  offerPrice: PropTypes.number,
  offerPriceType: PropTypes.string.isRequired,
  offerSlug: PropTypes.string,
  offerTitle: PropTypes.string,
  reviewRating: PropTypes.number,
  reviews: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      relevance: PropTypes.number.isRequired,
      text: PropTypes.string.isRequired,
      raw: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      likes: PropTypes.number.isRequired,
      statusName: PropTypes.string.isRequired,
      stars: PropTypes.number.isRequired,
      user: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        username: PropTypes.string.isRequired,
        photo: PropTypes.string.isRequired,
        typeName: PropTypes.string.isRequired,
        level: PropTypes.number.isRequired,
        specialityName: PropTypes.string,
        link: PropTypes.string.isRequired,
      }).isRequired,
    })
  ),
  storeDomain: PropTypes.string,
  storeName: PropTypes.string,
  subcategoryName: PropTypes.string,
  subcategorySlug: PropTypes.string,
  totalReviews: PropTypes.number,
};

export default Schema;
